import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { FileUp, Download, Trash2, History, FolderOpen } from "lucide-react";
import {
  HR_DOC_TYPES, type HrDocType, type InstructorDocument,
  uploadInstructorDocument, getSignedDocumentUrl, deleteInstructorDocument,
  docLabel, expirationState,
} from "@/lib/personnel/documents";

export default function DocumentsSection({
  instructor, documents, onChanged,
}: { instructor: any; documents: InstructorDocument[]; onChanged: () => void }) {
  const [docType, setDocType] = useState<HrDocType>("cpr_certification");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const current = documents.filter((d) => d.is_current);
  const superseded = documents.filter((d) => !d.is_current);
  const visible = showHistory ? documents : current;

  const upload = async (supersedesId?: string) => {
    if (!file) { toast({ title: "Pick a file first" }); return; }
    setBusy(true);
    try {
      await uploadInstructorDocument({
        instructorId: instructor.id, docType, file,
        expiresAt: expiresAt || null, notes: notes || null,
        supersedesId: supersedesId ?? null,
      });
      setFile(null); setNotes(""); setExpiresAt("");
      toast({ title: supersedesId ? "Document replaced" : "Document uploaded" });
      onChanged();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally { setBusy(false); }
  };

  const download = async (d: InstructorDocument) => {
    try { const url = await getSignedDocumentUrl(d, "download"); window.open(url, "_blank"); }
    catch (e: any) { toast({ title: "Download failed", description: e.message, variant: "destructive" }); }
  };

  const del = async (d: InstructorDocument) => {
    if (!confirm(`Remove ${d.file_name} from ${instructor.full_name}'s file? The file is retained for records.`)) return;
    try { await deleteInstructorDocument(d); toast({ title: "Document removed" }); onChanged(); }
    catch (e: any) { toast({ title: "Delete failed", description: e.message, variant: "destructive" }); }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><FolderOpen className="h-4 w-4" /> Instructor Documents</CardTitle>
        <CardDescription>Background checks, CPR, teaching cert, contractor agreement, insurance, resume. Version history preserved.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={docType} onValueChange={(v) => setDocType(v as HrDocType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {HR_DOC_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Expires (optional)</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">File</Label>
            <Input type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <Button size="sm" onClick={() => upload()} disabled={busy || !file}>
              <FileUp className="h-3.5 w-3.5 mr-1" /> Upload
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-xs text-muted-foreground">{current.length} current · {superseded.length} archived version{superseded.length === 1 ? "" : "s"}</div>
          <Button size="sm" variant="ghost" onClick={() => setShowHistory((v) => !v)}>
            <History className="h-3.5 w-3.5 mr-1" /> {showHistory ? "Hide history" : "Show history"}
          </Button>
        </div>

        {visible.length === 0 && <div className="text-xs text-muted-foreground">No documents yet.</div>}

        <ul className="divide-y">
          {visible.map((d) => {
            const exp = expirationState(d.expires_at);
            return (
              <li key={d.id} className="py-2 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{docLabel(d.doc_type)}</span>
                    {!d.is_current && <Badge variant="outline">archived</Badge>}
                    {exp.tone === "red" && <Badge variant="destructive">{exp.label}</Badge>}
                    {exp.tone === "amber" && <Badge className="bg-amber-500 hover:bg-amber-600">{exp.label}</Badge>}
                    {exp.tone === "gray" && d.expires_at && <Badge variant="secondary">{exp.label}</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {d.file_name} · Uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                    {d.notes ? ` · ${d.notes}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => download(d)}>
                    <Download className="h-3.5 w-3.5 mr-1" /> Download
                  </Button>
                  {d.is_current && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => {
                        setDocType(d.doc_type);
                        setExpiresAt(d.expires_at ?? "");
                        setNotes(d.notes ?? "");
                        const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
                        fileInput?.click();
                        // The next upload with this same doc_type will supersede via the "Replace" button below.
                        (window as any).__hrReplaceTarget = d.id;
                      }}>Replace</Button>
                      <Button size="sm" variant="ghost" onClick={() => del(d)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {(window as any).__hrReplaceTarget && (
          <div className="rounded-md border border-amber-500/40 bg-amber-50/40 p-2 text-xs flex items-center justify-between">
            <span>A replacement upload is queued for the selected document.</span>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => upload((window as any).__hrReplaceTarget).then(() => { (window as any).__hrReplaceTarget = null; })} disabled={busy || !file}>
                Confirm replace
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { (window as any).__hrReplaceTarget = null; setFile(null); }}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
